import React from "react";
import {Avatar, Box, Button, Chip, Paper, Typography} from "@mui/material";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import GpsFixedOutlined from "@mui/icons-material/GpsFixedOutlined";
import {useTranslation} from "react-i18next";
import {trackingName, trackingStateLabel} from "./tracking-state";

function locationAge(seconds, t) {
    if (seconds < 60) return t("CourierMapAge", {seconds});
    if (seconds < 3600) return t("CourierMapAgeMinutes", {minutes: Math.floor(seconds / 60)});
    if (seconds < 86400) return t("CourierMapAgeHours", {hours: Math.floor(seconds / 3600)});
    return t("CourierMapAgeDays", {days: Math.floor(seconds / 86400)});
}

function LocationDisclosure({location}) {
    const {t, i18n} = useTranslation();
    return <Box component="details" sx={{mt: 1, color: "text.secondary", fontSize: "0.75rem",
        "& summary": {cursor: "pointer", py: 0.5, width: "fit-content"}, "& summary:focus-visible": {outline: "2px solid", outlineColor: "primary.main"}}}>
        <summary>{t("CourierMapLocationDetails")}</summary>
        <Typography variant="caption" component="p" sx={{mt: 0.5, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere"}}>
            {t("CourierMapCoordinates", {latitude: location.latitude.toFixed(5), longitude: location.longitude.toFixed(5)})}
        </Typography>
        <Typography variant="caption" component="p">{t("CourierMapCaptured")}: {new Date(location.capturedAt).toLocaleString(i18n.language)}</Typography>
    </Box>;
}

function LocationFacts({point}) {
    const {t, i18n} = useTranslation();
    const captured = new Date(point.location.capturedAt).toLocaleString(i18n.language);
    return <Box sx={{mt: 1.5}}>
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: "action.hover"}}>
            <Typography variant="body2" sx={{display: "flex", alignItems: "center", gap: 0.75,
                color: point.poorAccuracy ? "warning.dark" : "text.secondary"}}>
                <GpsFixedOutlined sx={{fontSize: 17}}/>{t("CourierMapAccuracy", {accuracy: point.location.accuracy})}
            </Typography>
            <Typography component="time" dateTime={point.location.capturedAt} title={captured} variant="body2"
                aria-label={`${t("CourierMapCaptured")}: ${captured}`}
                sx={{display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary"}}>
                <AccessTimeOutlined sx={{fontSize: 17}}/>{locationAge(point.age, t)}
            </Typography>
        </Box>
        {point.poorAccuracy && <Typography variant="caption" color="warning.dark">{t("CourierMapPoorAccuracy")}</Typography>}
        <LocationDisclosure location={point.location}/>
    </Box>;
}

export default function CourierLocationCard({point, onDetails, onFilterCourier, filterLabel}) {
    const {t} = useTranslation();
    const name = trackingName(point) || t("CourierProfile");
    const initials = [point.firstName, point.lastName].filter(Boolean).map(part => part.slice(0, 1)).join("");
    const color = point.state === "fresh" ? "success" : ["stale", "expired"].includes(point.state) ? "warning" : "default";
    return <Paper component="li" variant="outlined" sx={{p: 2, borderRadius: 2, minWidth: 0,
        borderColor: point.state === "stale" ? "warning.light" : "divider"}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 1.25, minWidth: 0}}>
            <Avatar aria-hidden="true" sx={{width: 36, height: 36, fontSize: 14, fontWeight: 700,
                bgcolor: "action.selected", color: "text.secondary"}}>{initials || "•"}</Avatar>
            <Button onClick={() => onDetails(point.courierId)} sx={{p: 0, minWidth: 0, justifyContent: "flex-start",
                textAlign: "left", textTransform: "none", fontSize: "0.95rem", fontWeight: 700, overflowWrap: "anywhere"}}>{name}</Button>
        </Box>
        <Chip size="small" color={color} variant="outlined" label={t(trackingStateLabel(point.state))}
            sx={{mt: 1.25, height: "auto", maxWidth: "100%", "& .MuiChip-label": {whiteSpace: "normal", py: 0.5, lineHeight: 1.4}}}/>
        {point.progress && <Typography variant="body2" sx={{mt: 1, fontWeight: 500}}>{point.progress}</Typography>}
        {point.location && <LocationFacts point={point}/>}
        <Box sx={{display: "flex", justifyContent: "flex-end", borderTop: 1, borderColor: "divider", mt: 1.5, pt: 1.25}}>
            <Button size="small" variant="outlined" startIcon={<FilterAltOutlined sx={{fontSize: 16}}/>}
                aria-label={`${t(filterLabel)}: ${name}`} onClick={() => onFilterCourier({id: point.courierId, name})}
                sx={{textTransform: "none", borderRadius: 1.5, whiteSpace: "normal"}}>{t(filterLabel)}</Button>
        </Box>
    </Paper>;
}
